import React, { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Send, Bot, User, Sparkles, Trash2 } from "lucide-react";
import axios from "axios";

const AIAssistant = () => {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, [user?.active_company_id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const history = response.data.history || [];
      const formattedMessages = history.flatMap(h => [
        { role: "user", content: h.user_message },
        { role: "assistant", content: h.ai_response }
      ]);
      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Kunde inte skicka meddelande");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Tyvärr uppstod ett fel. Försök igen." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  const suggestedQuestions = [
    "Hur bokför jag en leverantörsfaktura?",
    "Vilka konton ska jag använda för kontorsmaterial?",
    "Hur beräknar jag arbetsgivaravgifter?",
    "Vad är skillnaden mellan debet och kredit?",
    "Hur hanterar jag moms vid försäljning?"
  ];

  const handleSuggestion = (question) => {
    setInput(question);
  };

  if (historyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col animate-fadeIn" data-testid="ai-assistant">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl ai-gradient flex items-center justify-center">
            <Bot className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">AI Assistent</h1>
            <p className="text-slate-500">Fråga mig om bokföring och redovisning</p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" onClick={clearChat} data-testid="clear-chat-btn">
            <Trash2 size={16} className="mr-2" />
            Rensa chatt
          </Button>
        )}
      </div>

      {/* Chat Container */}
      <Card className="card-base flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-6" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 rounded-2xl ai-gradient flex items-center justify-center mb-6">
                <Sparkles className="text-white" size={40} />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Hej! Jag är din redovisningsassistent
              </h2>
              <p className="text-slate-500 mb-8 max-w-md">
                Jag kan hjälpa dig med bokföringsfrågor, förklara BAS-kontoplanen, 
                och ge råd om svensk redovisning.
              </p>
              <div className="w-full max-w-lg">
                <p className="text-sm text-slate-500 mb-3">Förslag på frågor:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestedQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestion(question)}
                      className="text-sm px-4 py-2 rounded-full bg-slate-100 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                      data-testid={`suggestion-${idx}`}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                  data-testid={`message-${idx}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === "user" 
                      ? "bg-indigo-600" 
                      : "ai-gradient"
                  }`}>
                    {message.role === "user" 
                      ? <User className="text-white" size={16} />
                      : <Bot className="text-white" size={16} />
                    }
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === "user"
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-900"
                  }`}>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {message.content}
                    </p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3" data-testid="loading-indicator">
                  <div className="w-8 h-8 rounded-full ai-gradient flex items-center justify-center">
                    <Bot className="text-white" size={16} />
                  </div>
                  <div className="bg-slate-100 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-slate-200">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Skriv din fråga här..."
              className="flex-1"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              className="bg-indigo-600 hover:bg-indigo-700" 
              disabled={loading || !input.trim()}
              data-testid="send-btn"
            >
              <Send size={18} />
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default AIAssistant;
